import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import { useHistory } from "react-router-dom";
import { QrCode, TriangleAlert } from "lucide-react";

import { Skeleton, Button, Dialog } from "@/components/ui";
import { PlayerCardServices } from "@/services/bedrock";
import { URLS } from "@/utils";
import { PlayerQRCode } from "@/components/PlayerQRCode";

interface SharePlayerCardDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  playerId: number;
}

export const SharePlayerCardDialog: React.FC<SharePlayerCardDialogProps> = ({ open, onOpenChange, playerId }) => {
  const history = useHistory();

  // Fetch Player Card Data self-contained
  const { data: playerCard, isLoading: isPlayerCardLoading } = useQuery({
    queryKey: ["mainPlayerCard", playerId],
    queryFn: () => PlayerCardServices.getMainPlayerCard(playerId),
    enabled: !!playerId && open,
  });

  const playerCardUrl = playerCard ? `${playerCard.baseUrl.replace(/\/$/, "")}/${playerCard.slug}` : "";
  const qrLogoSrc = playerCard?.player?.photoUrl;

  return (
    <Dialog open={open} onOpenChange={onOpenChange} size="md" showCloseButton>
      <Dialog.Header>
        <Dialog.Title className="flex items-center gap-2 text-slate-800 font-bold">
          <QrCode className="size-5 text-indigo-600 shrink-0" />
          Share Player Card
        </Dialog.Title>
      </Dialog.Header>

      <Dialog.Content className="flex flex-col items-center gap-5">
        {isPlayerCardLoading ? (
          <div className="flex flex-col items-center gap-4 w-full py-4 animate-pulse">
            <Skeleton className="size-48 rounded-xl" />
            <Skeleton className="h-10 w-full rounded-lg" />
          </div>
        ) : !playerCard ? (
          <div className="flex flex-col items-center gap-4 w-full text-center py-4 px-2">
            <TriangleAlert className="size-12 text-amber-500 shrink-0" />
            <div>
              <h3 className="text-base font-bold text-slate-800 mb-1">No Player Card Created</h3>
              <p className="text-sm text-slate-500 leading-normal">
                You must first set up and publish your social business card to share it.
              </p>
            </div>
            <Button
              variant="default"
              onClick={() => {
                onOpenChange(false);
                history.push(`${URLS.player.playerCards}?playerId=${playerId}&create=true`);
              }}
              className="mt-2"
            >
              Create Player Card Now &rarr;
            </Button>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-5 w-full min-w-0">
            {/* Fully Encapsulated Player QR Code & Sharing Actions Component */}
            <PlayerQRCode
              id="qr-code-svg"
              value={playerCardUrl}
              qrLogoSrc={qrLogoSrc}
              title={`${playerCard?.player?.firstName || "My"} Social Business Card`}
              text="Check out my personalized social business card!"
              downloadFileName={`${playerCard?.slug || "playercard"}-qr-code.png`}
            />
          </div>
        )}
      </Dialog.Content>
    </Dialog>
  );
};
